// BRIEF 102 §2f — ONE definition of the day counter, and it was already two.
//
// MOVED, NOT REWRITTEN. §2f said to take the Agenda list's existing form rather
// than invent one:
//
//   AgendaView.tsx:513-523   diff === 0 → t("agenda.today")
//                            diff > 0   → `-${diff}d`
//                            diff < 0   → `+${Math.abs(diff)}d`
//
// and it was ALREADY written twice — `CaseEventRow.tsx:124-136` computes the
// same three branches off the same `Math.round(… / 864e5)`, with the same sign
// convention, for the same label. So sharing it is not a new abstraction: it is
// the drain 101 §1 performed on `DateBlock`, on the value beside it.
//
// ── THE SIGN IS DELIBERATE AND IS NOT CORRECTED ─────────────────────────────
// A FUTURE date renders `-3d`. It is a COUNTDOWN — three days left — not an
// offset from today, which is why `diff > 0` (the date is ahead) prints a minus.
// §2f names this explicitly and forbids "correcting" it. Both existing call
// sites agree, so there is no disagreement to resolve, only a convention to
// carry.
//
// ── WHAT THIS FILE DOES NOT DECIDE ──────────────────────────────────────────
// COLOUR. `CaseEventRow` colours its label through `evDayClr(diff, ev.type)`
// and the Agenda list has its own ladder; both keep them. This returns the
// diff and the words, and every host styles its own.

// ── AND THE `any`s DID NOT COME WITH IT ─────────────────────────────────────
// Both call sites spelled the subtraction `(new Date(x) as any) - (new Date(y)
// as any)`, which is what `.getTime()` means in a file that has types.
// `scripts/lint/ratchets.js` refuses a new file that adds `any`, and re-record
// is for a MOVE — this is a few lines lifted out of two. Same arithmetic, real
// types. `TFn` is the translator type the rest of src/lib already imports.

import type { TFn } from "./taxonomyTypes";

/** Whole days from `todayISO` to `dateISO`; positive = in the future. */
export const dayDiff = (
  dateISO: string | Date | null | undefined,
  todayISO: string,
): number | null => {
  const d = dateISO ? String(dateISO).slice(0, 10) : "";
  if (!d) return null;
  return Math.round((new Date(d).getTime() - new Date(todayISO).getTime()) / 864e5);
};

/**
 * The Agenda list's label, verbatim: "Today", or a countdown (`-3d` ahead,
 * `+3d` behind). `null` for a row with no date — the caller renders nothing,
 * and §4a's row must not jump when it does.
 */
export const dayCountdownLabel = (
  diff: number | null,
  t: TFn,
): string | null =>
  diff === null
    ? null
    : diff === 0
      ? t("agenda.today", { fallback: "Today" })
      : diff > 0
        ? `-${diff}d`
        : `+${Math.abs(diff)}d`;
